import { Table, Input, Button, Space, message, Modal } from "antd";
import { PlusOutlined, LeftOutlined, BulbOutlined } from "@ant-design/icons";
import { Edit, Trash2 } from "lucide-react";
import { useState, useEffect } from "react";
import Designation from '../../../../../modals/addDesignation';
import EditDesignation from '../../../../../modals/editDesignation';
import ModalWrapper from '../../../../../Modal';
import { getRequest, deleteRequest } from "../../../../../service/index";
import { getEmployeeData } from "../../../../../utils/localStorage";

const { Search } = Input;

const Designations = () => {
  const [pageSize, setPageSize] = useState(10);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedDesignation, setSelectedDesignation] = useState(null);

  const employeeData = getEmployeeData();
  const userRole = employeeData?.role;
  const designationPermissions = employeeData?.designation?.permissions?.Organization?.Designation || [];

  const canView = userRole === "companyadmin" || designationPermissions.includes("view");
  const canAdd = userRole === "companyadmin" || designationPermissions.includes("add");
  const canEdit = userRole === "companyadmin" || designationPermissions.includes("edit");
  const canDelete = userRole === "companyadmin" || designationPermissions.includes("delete");

  // Fetch designations from API
  const fetchDesignations = async () => {
    setLoading(true);
    try {
      if (!canView) {
        setDesignations([]);
      } else {
        const response = await getRequest('/api/companies/designation');
        if (response.designations) {
          // Transform the data to match the table structure
          const transformedData = response.designations.map((item, index) => ({
            id: item._id,
            key: item._id,
            name: item.designationName,
            employees: item.employeeCount || 0,
            role: item.role,
            permissions: item.permissions
          }));
          setDesignations(transformedData);
        }
      }
    } catch (error) {
      console.error('Error fetching designations:', error);
      message.error('Failed to fetch designations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDesignations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  // Handle delete designation
  const handleDelete = (record) => {
    if (!canDelete) {
      message.warning("You don't have permission to delete designations.");
      return;
    }
    Modal.confirm({
      title: 'Are you sure you want to delete this designation?',
      content: `This will permanently delete the designation "${record.name}" and all its associated data.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No, Cancel',
      onOk: async () => {
        try {
          const response = await deleteRequest(`/api/companies/designation?designationId=${record.id}`);
          if (response.message === "Designation deleted successfully") {
            message.success('Designation deleted successfully');
            fetchDesignations(); // Refresh the list
          } else {
            message.error(response.error || 'Failed to delete designation');
          }
        } catch (error) {
          console.error('Error deleting designation:', error);
          message.error('Failed to delete designation');
        }
      },
    });
  };

  // Handle edit designation
  const handleEdit = (record) => {
    if (!canEdit) {
      message.warning("You don't have permission to edit designations.");
      return;
    }
    setSelectedDesignation(record);
    setEditModalOpen(true);
  };

  // Handle add designation
  const handleAdd = () => {
    setAddModalOpen(true);
  };

  // Handle modal close
  const handleCloseAddModal = () => {
    setAddModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedDesignation(null);
  };

  // Filter designations based on search
  const filteredDesignations = designations.filter(designation =>
    designation.name?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Employees",
      dataIndex: "employees",
      key: "employees",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<Edit className="h-4 w-4" />}
            disabled={!canEdit}
            onClick={() => handleEdit(record)}
            className="text-blue-600 hover:text-blue-800"
          />
          <Button
            type="text"
            icon={<Trash2 className="h-4 w-4" />}
            disabled={!canDelete}
            onClick={() => handleDelete(record)}
            className="text-red-600 hover:text-red-800"
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="p-4 bg-white rounded-lg shadow flex flex-col gap-5">
        <div className="flex justify-between items-center">
          <h4 className="font-semibold">Designations</h4>
          <Space className="mt-4 justify-end w-full">
            <Button icon={<LeftOutlined />} type="default">
              Back
            </Button>
            <Button icon={<BulbOutlined />}>Page Tip</Button>
          </Space>
        </div>

        <div className="flex justify-between items-center gap-4">
          <Search 
            placeholder="Search" 
            allowClear 
            className="w-full max-w-sm"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="bg-primary text-white"
            disabled={!canAdd}
            onClick={() => canAdd ? handleAdd() : message.warning("You don't have permission to add designations.")}
          >
            Add Designation
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={canView ? filteredDesignations : []}
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{
            total: canView ? filteredDesignations.length : 0,
            pageSize: pageSize,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} designations`,
            onShowSizeChange: (_, size) => setPageSize(size),
          }}
          className="bg-white rounded-lg border border-gray-200"
        />
      </div>

      {/* Add Designation Modal */}
      <ModalWrapper
        open={addModalOpen}
        onRequestClose={handleCloseAddModal}
        title="Add Designation"
      >
        <Designation onClose={handleCloseAddModal} onSuccess={fetchDesignations} />
      </ModalWrapper>

      {/* Edit Designation Modal */}
      <ModalWrapper
        open={editModalOpen}
        onRequestClose={handleCloseEditModal}
        title="Edit Designation"
      >
        <EditDesignation 
          onClose={handleCloseEditModal} 
          designation={selectedDesignation}
          onSuccess={fetchDesignations}
        />
      </ModalWrapper>
      {!canView && (
        <div className="text-center text-gray-500 py-4">You don't have permission to view designations.</div>
      )}
    </>
  );
};

export default Designations;
